import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AliasSuggestion,
  Person,
  PersonAlias,
  PersonProvider,
  ProviderAccountCandidate,
} from '@zunelo/shared';
import { peopleClient } from '../../services/clients';
import { Button } from '../../ui/common/Button';
import { invalidatePeopleMentionCandidates } from '../../hooks/usePeopleMentionCandidates';
import './settingsPeople.css';

const PROVIDER_LABELS: Record<PersonProvider, string> = {
  slack: 'Slack',
  jira: 'Jira',
  github: 'GitHub',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 9,
  border: '1px solid var(--z-border)',
  background: 'var(--z-surface)',
  color: 'var(--z-text)',
  font: 'inherit',
} as const;

function providerLabel(provider: PersonProvider): string {
  return PROVIDER_LABELS[provider];
}

function accountLabel(account: ProviderAccountCandidate | PersonAlias): string {
  const username = account.username?.trim().replace(/^@/, '');
  return username ? `@${username}` : account.displayName;
}

function errorMessage(cause: unknown, fallback: string): string {
  if (!(cause instanceof Error)) return fallback;
  const knownMessages: Record<string, string> = {
    PERSON_ALIAS_CLAIMED: 'Ese alias ya está vinculado a otra persona.',
    PERSON_ALIAS_NOT_FOUND: 'Ese alias ya no está disponible.',
    PERSON_NOT_FOUND: 'La persona ya no existe. Actualiza la lista.',
    PERSON_REVISION_CONFLICT:
      'La persona cambió en otra ventana. Actualiza la lista e inténtalo de nuevo.',
    PERSON_PROVIDER_UNAVAILABLE:
      'No se pudo buscar en la plataforma. Revisa su conexión e inténtalo de nuevo.',
  };
  return (knownMessages[cause.message] ?? cause.message) || fallback;
}

function candidateKey(candidate: ProviderAccountCandidate): string {
  return `${candidate.platform}:${candidate.externalId}`;
}

function ProviderAccountAutocomplete({
  platform,
  query,
  candidates,
  searching,
  onQueryChange,
  onSubmit,
  onSelect,
}: {
  platform: PersonProvider;
  query: string;
  candidates: ProviderAccountCandidate[];
  searching: boolean;
  onQueryChange: (value: string) => void;
  onSubmit: () => void;
  onSelect: (candidate: ProviderAccountCandidate) => void;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = 'settings-people-provider-results';
  const showResults = query.trim().length >= 2;

  useEffect(() => {
    setActiveIndex(candidates.length ? 0 : -1);
  }, [candidates]);

  const selectActive = () => {
    const candidate = candidates[activeIndex];
    if (candidate) onSelect(candidate);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && candidates.length) {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % candidates.length);
      return;
    }
    if (event.key === 'ArrowUp' && candidates.length) {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + candidates.length) % candidates.length);
      return;
    }
    if (event.key === 'Enter' && candidates.length && activeIndex >= 0) {
      event.preventDefault();
      selectActive();
      return;
    }
    if (event.key === 'Escape') onQueryChange('');
  };

  return (
    <div className="z-settings-people__autocomplete" aria-busy={searching}>
      <div className="z-settings-people__search-row">
        <input
          id="settings-people-provider-search"
          type="search"
          role="combobox"
          aria-label="Buscar una cuenta o alias"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={showResults}
          aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Busca por nombre o alias de ${providerLabel(platform)}`}
          style={inputStyle}
        />
        <Button
          size="sm"
          variant="primary"
          onClick={onSubmit}
          loading={searching}
          disabled={query.trim().length < 2}
          className="z-settings-people__button"
        >
          Buscar
        </Button>
      </div>
      {showResults ? (
        <div id={listId} role="listbox" className="z-settings-people__results">
          {searching ? (
            <div className="z-settings-people__result-note">
              Buscando en {providerLabel(platform)}…
            </div>
          ) : candidates.length ? (
            candidates.map((candidate, index) => (
              <button
                type="button"
                role="option"
                id={`${listId}-${index}`}
                aria-selected={index === activeIndex}
                key={candidateKey(candidate)}
                className={`z-settings-people__result ${index === activeIndex ? 'is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => onSelect(candidate)}
              >
                <span className="z-settings-people__result-main">{candidate.displayName}</span>
                <span className="z-settings-people__result-meta">
                  {accountLabel(candidate)} · {providerLabel(candidate.platform)}
                </span>
              </button>
            ))
          ) : (
            <div className="z-settings-people__result-note">
              No encontramos cuentas o aliases que coincidan.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AliasRow({
  alias,
  preferred,
  onRemove,
  onSetPreferred,
}: {
  alias: PersonAlias;
  preferred: boolean;
  onRemove: () => void;
  onSetPreferred: () => void;
}) {
  return (
    <div className="z-settings-people__alias-row">
      <div className="z-settings-people__alias-copy">
        <strong>{providerLabel(alias.platform)}</strong>
        <span>{alias.displayName}</span>
        {alias.username ? <small>{accountLabel(alias)}</small> : null}
      </div>
      <div className="z-settings-people__alias-actions">
        {preferred ? (
          <span className="z-settings-people__preferred">Preferido</span>
        ) : (
          <button type="button" className="z-settings-people__text-button" onClick={onSetPreferred}>
            Usar para mencionar
          </button>
        )}
        <button
          type="button"
          className="z-settings-people__text-button z-settings-people__text-button--danger"
          onClick={onRemove}
        >
          Quitar
        </button>
      </div>
    </div>
  );
}

function SuggestionList({
  suggestions,
  people,
  onAccept,
  onReject,
}: {
  suggestions: AliasSuggestion[];
  people: Person[];
  onAccept: (suggestion: AliasSuggestion) => void;
  onReject: (suggestion: AliasSuggestion) => void;
}) {
  if (!suggestions.length) return null;
  return (
    <section className="z-settings-people__suggestions" data-testid="settings-people-suggestions">
      <div>
        <h3>Aliases sugeridos</h3>
        <p>Revisa estas coincidencias antes de confirmarlas. Nunca se vinculan automáticamente.</p>
      </div>
      <div className="z-settings-people__suggestion-list">
        {suggestions.map((suggestion) => {
          const person = people.find((item) => item.id === suggestion.personId);
          return (
            <div key={suggestion.id} className="z-settings-people__suggestion">
              <div>
                <strong>{suggestion.candidate.displayName}</strong>
                <span>
                  {providerLabel(suggestion.candidate.platform)} · para{' '}
                  {person?.displayName ?? 'una persona'}
                </span>
                <small>
                  {suggestion.evidence.kind === 'email_exact'
                    ? 'Coincidencia exacta de correo'
                    : 'Encontrado durante una búsqueda'}
                </small>
              </div>
              <div className="z-settings-people__suggestion-actions">
                <Button
                  size="sm"
                  variant="primary"
                  className="z-settings-people__button"
                  onClick={() => onAccept(suggestion)}
                >
                  Confirmar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="z-settings-people__button"
                  onClick={() => onReject(suggestion)}
                >
                  Descartar
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function SettingsPeopleSection() {
  const [people, setPeople] = useState<Person[]>([]);
  const [suggestions, setSuggestions] = useState<AliasSuggestion[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newPersonName, setNewPersonName] = useState('');
  const [editName, setEditName] = useState('');
  const [platform, setPlatform] = useState<PersonProvider>('slack');
  const [manualExternalId, setManualExternalId] = useState('');
  const [manualAliasName, setManualAliasName] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [peopleQuery, setPeopleQuery] = useState('');
  const [candidates, setCandidates] = useState<ProviderAccountCandidate[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<ProviderAccountCandidate | null>(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [mergeSourceId, setMergeSourceId] = useState('');
  const [splitAliasIds, setSplitAliasIds] = useState<string[]>([]);
  const [splitName, setSplitName] = useState('');
  const [reassignAliasId, setReassignAliasId] = useState('');
  const [reassignTargetId, setReassignTargetId] = useState('');
  const searchRequestRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [peopleResult, suggestionResult] = await Promise.all([
        peopleClient.listPeople(),
        peopleClient.listSuggestions(),
      ]);
      setPeople(peopleResult.data);
      setSuggestions(suggestionResult.data);
      setSelectedId((current) =>
        current && peopleResult.data.some((person) => person.id === current)
          ? current
          : (peopleResult.data[0]?.id ?? null),
      );
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudieron cargar las personas.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = people.find((person) => person.id === selectedId) ?? null;
  const selectedName = selected?.displayName ?? '';

  useEffect(() => {
    setEditName(selectedName);
    setSelectedCandidate(null);
    setSearchQuery('');
    setCandidates([]);
    setSplitAliasIds([]);
    setReassignAliasId('');
    setReassignTargetId('');
  }, [selectedId, selectedName]);

  const visiblePeople = useMemo(() => {
    const query = peopleQuery.trim().toLowerCase();
    if (!query) return people;
    return people.filter((person) => {
      const searchable = [
        person.displayName,
        ...person.aliases.flatMap((alias) => [alias.displayName, alias.username ?? '']),
      ]
        .join(' ')
        .toLowerCase();
      return searchable.includes(query);
    });
  }, [people, peopleQuery]);

  const availableCandidates = useMemo(() => {
    if (!selected) return candidates;
    return candidates.filter(
      (candidate) =>
        !selected.aliases.some(
          (alias) =>
            alias.platform === candidate.platform && alias.externalId === candidate.externalId,
        ),
    );
  }, [candidates, selected]);

  const replacePerson = (nextPerson: Person) => {
    setPeople((current) =>
      current.map((person) => (person.id === nextPerson.id ? nextPerson : person)),
    );
    invalidatePeopleMentionCandidates();
  };

  const selectPerson = (personId: string) => {
    setSelectedId(personId);
    setError(null);
    setStatus(null);
  };

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = newPersonName.trim();
    if (!name) {
      setError('Escribe un nombre para crear la persona.');
      return;
    }
    try {
      const result = await peopleClient.createPerson({ displayName: name });
      setPeople((current) => [...current, result.data]);
      invalidatePeopleMentionCandidates();
      setSelectedId(result.data.id);
      setNewPersonName('');
      setStatus('Persona creada. Ahora puedes vincular sus aliases.');
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudo crear la persona.'));
    }
  };

  const saveName = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !editName.trim()) return;
    try {
      const result = await peopleClient.updatePerson(selected.id, {
        displayName: editName.trim(),
        expectedRevision: selected.revision,
      });
      replacePerson(result.data);
      setStatus('Nombre guardado.');
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudo guardar el nombre.'));
    }
  };

  const attachCandidate = async (candidate: ProviderAccountCandidate) => {
    if (!selected) return;
    try {
      const result = await peopleClient.attachAlias(selected.id, candidate, selected.revision);
      replacePerson(result.data);
      setSelectedCandidate(null);
      setSearchQuery('');
      setCandidates([]);
      setManualExternalId('');
      setManualAliasName('');
      setStatus(`Alias de ${providerLabel(candidate.platform)} vinculado.`);
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudo vincular el alias.'));
    }
  };

  const attachManual = async (event: React.FormEvent) => {
    event.preventDefault();
    const externalId = manualExternalId.trim();
    const displayName = manualAliasName.trim();
    if (!externalId || !displayName) {
      setError('Completa el alias y el identificador técnico para vincularlo manualmente.');
      return;
    }
    await attachCandidate({
      platform,
      externalId,
      displayName,
      refreshedAt: new Date().toISOString(),
      ...(platform === 'github' ? { username: displayName } : {}),
    });
  };

  const runProviderSearch = useCallback(async (query: string, searchPlatform: PersonProvider) => {
    const normalized = query.trim();
    const requestId = ++searchRequestRef.current;
    if (normalized.length < 2) {
      setCandidates([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const result = await peopleClient.searchProviderAccounts(searchPlatform, normalized);
      if (requestId === searchRequestRef.current) setCandidates(result.data);
    } catch (cause) {
      if (requestId === searchRequestRef.current) {
        setCandidates([]);
        setError(errorMessage(cause, 'No se pudo buscar en la plataforma.'));
      }
    } finally {
      if (requestId === searchRequestRef.current) setSearching(false);
    }
  }, []);

  useEffect(() => {
    const normalized = searchQuery.trim();
    searchRequestRef.current += 1;
    if (normalized.length < 2) {
      setCandidates([]);
      setSearching(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      void runProviderSearch(searchQuery, platform);
    }, 240);
    return () => window.clearTimeout(timeoutId);
  }, [platform, runProviderSearch, searchQuery]);

  const submitProviderSearch = () => {
    if (searchQuery.trim().length < 2) {
      setError('Escribe al menos 2 caracteres para buscar.');
      return;
    }
    void runProviderSearch(searchQuery, platform);
  };

  const removeAlias = async (alias: PersonAlias) => {
    if (!selected) return;
    if (
      !window.confirm(
        `¿Quitar el alias de ${providerLabel(alias.platform)} de ${selected.displayName}?`,
      )
    ) {
      return;
    }
    try {
      const result = await peopleClient.detachAlias(selected.id, alias.id, selected.revision);
      replacePerson(result.data);
      setStatus('Alias quitado.');
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudo quitar el alias.'));
    }
  };

  const setPreferred = async (alias: PersonAlias) => {
    if (!selected) return;
    try {
      const result = await peopleClient.setPreferredAlias(
        selected.id,
        alias.platform,
        alias.id,
        selected.revision,
      );
      replacePerson(result.data);
      setStatus(`Alias preferido de ${providerLabel(alias.platform)} actualizado.`);
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudo actualizar el alias preferido.'));
    }
  };

  const removePerson = async () => {
    if (!selected) return;
    if (
      !window.confirm(
        `¿Eliminar definitivamente a ${selected.displayName}? Esta acción no se puede deshacer.`,
      )
    ) {
      return;
    }
    try {
      await peopleClient.deletePerson(selected.id, selected.revision);
      const remaining = people.filter((person) => person.id !== selected.id);
      setPeople(remaining);
      setSelectedId(remaining[0]?.id ?? null);
      invalidatePeopleMentionCandidates();
      setStatus('Persona eliminada.');
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudo eliminar la persona.'));
    }
  };

  const accept = async (suggestion: AliasSuggestion) => {
    const person = people.find((item) => item.id === suggestion.personId);
    if (!person) return;
    try {
      const result = await peopleClient.acceptSuggestion(
        suggestion.id,
        person.revision,
        suggestion.candidate.externalId,
      );
      replacePerson(result.data);
      setSuggestions((current) => current.filter((item) => item.id !== suggestion.id));
      setStatus('Alias sugerido confirmado.');
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudo confirmar el alias sugerido.'));
    }
  };

  const reject = async (suggestion: AliasSuggestion) => {
    try {
      await peopleClient.rejectSuggestion(suggestion.id);
      setSuggestions((current) => current.filter((item) => item.id !== suggestion.id));
      setStatus('Alias sugerido descartado.');
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudo descartar el alias sugerido.'));
    }
  };

  const merge = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !mergeSourceId) return;
    const source = people.find((person) => person.id === mergeSourceId);
    if (!source) return;
    if (!window.confirm(`¿Unir ${source.displayName} dentro de ${selected.displayName}?`)) return;
    try {
      const result = await peopleClient.mergePeople(selected.id, [source.id], selected.revision);
      setPeople((current) =>
        current
          .filter((person) => person.id !== source.id)
          .map((person) => (person.id === result.data.id ? result.data : person)),
      );
      invalidatePeopleMentionCandidates();
      setMergeSourceId('');
      setStatus('Personas unidas.');
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudieron unir las personas.'));
    }
  };

  const split = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !splitAliasIds.length) return;
    if (!window.confirm('¿Mover los aliases seleccionados a una nueva persona?')) return;
    try {
      await peopleClient.splitPerson(
        selected.id,
        splitAliasIds,
        selected.revision,
        splitName.trim() || undefined,
      );
      await load();
      invalidatePeopleMentionCandidates();
      setSplitAliasIds([]);
      setSplitName('');
      setStatus('Aliases separados en una nueva persona.');
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudieron separar los aliases.'));
    }
  };

  const reassign = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !reassignAliasId || !reassignTargetId) return;
    const target = people.find((person) => person.id === reassignTargetId);
    if (!target) return;
    if (!window.confirm(`¿Mover este alias a ${target.displayName}?`)) return;
    try {
      await peopleClient.reassignAlias(target.id, reassignAliasId, selected.id, target.revision);
      await load();
      invalidatePeopleMentionCandidates();
      setReassignAliasId('');
      setReassignTargetId('');
      setStatus('Alias reasignado.');
    } catch (cause) {
      setError(errorMessage(cause, 'No se pudo reasignar el alias.'));
    }
  };

  const otherPeople = people.filter((person) => person.id !== selected?.id);

  return (
    <section
      data-testid="settings-people-section"
      aria-labelledby="settings-people-title"
      className="z-settings-people"
    >
      <header className="z-settings-people__intro">
        <div>
          <p className="z-settings-people__eyebrow">Identidad</p>
          <h2 id="settings-people-title">Personas</h2>
          <p>
            Agrupa los aliases de una misma persona para mencionarla correctamente en cada
            plataforma.
          </p>
        </div>
        <span className="z-settings-people__count" aria-label={`${people.length} personas`}>
          {people.length}
        </span>
      </header>

      {error ? (
        <div role="alert" className="z-settings-people__message z-settings-people__message--error">
          {error}
        </div>
      ) : null}
      {status ? (
        <div
          role="status"
          aria-live="polite"
          className="z-settings-people__message z-settings-people__message--success"
        >
          {status}
        </div>
      ) : null}

      <SuggestionList
        suggestions={suggestions}
        people={people}
        onAccept={accept}
        onReject={reject}
      />

      <form onSubmit={create} className="z-settings-people__create-form">
        <label htmlFor="settings-people-new-name" className="z-settings-people__field-label">
          Nueva persona
        </label>
        <div className="z-settings-people__create-row">
          <input
            id="settings-people-new-name"
            aria-label="Nombre de la nueva persona"
            value={newPersonName}
            onChange={(event) => setNewPersonName(event.target.value)}
            placeholder="Por ejemplo, Ada Lovelace"
            style={inputStyle}
          />
          <Button type="submit" size="sm" variant="primary" className="z-settings-people__button">
            Crear persona
          </Button>
        </div>
      </form>

      {loading ? (
        <p className="z-settings-people__empty">Cargando personas…</p>
      ) : people.length === 0 ? (
        <p data-testid="settings-people-empty" className="z-settings-people__empty">
          Todavía no hay personas. Crea una para empezar a vincular aliases.
        </p>
      ) : (
        <div className="z-settings-people__layout">
          <aside className="z-settings-people__rail" aria-label="Lista de personas">
            <label htmlFor="settings-people-filter" className="z-settings-people__field-label">
              Buscar personas
            </label>
            <input
              id="settings-people-filter"
              type="search"
              aria-label="Buscar personas"
              value={peopleQuery}
              onChange={(event) => setPeopleQuery(event.target.value)}
              placeholder="Nombre o alias…"
              style={inputStyle}
            />
            <div role="listbox" aria-label="Personas" className="z-settings-people__list">
              {visiblePeople.length ? (
                visiblePeople.map((person) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected={person.id === selectedId}
                    key={person.id}
                    onClick={() => selectPerson(person.id)}
                    className={`z-settings-people__person ${person.id === selectedId ? 'is-selected' : ''}`}
                  >
                    <span>{person.displayName}</span>
                    <small>
                      {person.aliases.length === 1
                        ? '1 alias confirmado'
                        : `${person.aliases.length} aliases confirmados`}
                    </small>
                  </button>
                ))
              ) : (
                <p className="z-settings-people__empty">No hay coincidencias.</p>
              )}
            </div>
          </aside>

          {selected ? (
            <div className="z-settings-people__detail">
              <div className="z-settings-people__detail-header">
                <div>
                  <p className="z-settings-people__eyebrow">Persona seleccionada</p>
                  <h3>{selected.displayName}</h3>
                </div>
                <button
                  type="button"
                  onClick={() => void removePerson()}
                  className="z-settings-people__text-button z-settings-people__text-button--danger"
                >
                  Eliminar persona
                </button>
              </div>

              <form onSubmit={saveName} className="z-settings-people__name-form">
                <label
                  htmlFor="settings-people-edit-name"
                  className="z-settings-people__field-label"
                >
                  Nombre visible
                </label>
                <div className="z-settings-people__create-row">
                  <input
                    id="settings-people-edit-name"
                    aria-label="Nombre visible de la persona"
                    value={editName}
                    onChange={(event) => setEditName(event.target.value)}
                    style={inputStyle}
                  />
                  <Button
                    type="submit"
                    size="sm"
                    variant="ghost"
                    className="z-settings-people__button"
                  >
                    Guardar nombre
                  </Button>
                </div>
              </form>

              <section
                className="z-settings-people__block"
                aria-labelledby="confirmed-aliases-title"
              >
                <div className="z-settings-people__block-heading">
                  <div>
                    <h4 id="confirmed-aliases-title">Aliases confirmados</h4>
                    <p>Estos son los aliases que se usarán al enviar una mención.</p>
                  </div>
                  <span>{selected.aliases.length}</span>
                </div>
                {selected.aliases.length === 0 ? (
                  <p data-testid="settings-people-no-aliases" className="z-settings-people__empty">
                    Aún no hay aliases. Busca una cuenta de la plataforma para poder mencionarla.
                  </p>
                ) : (
                  <div className="z-settings-people__alias-list">
                    {selected.aliases.map((alias) => (
                      <AliasRow
                        key={alias.id}
                        alias={alias}
                        preferred={selected.preferredAliasIds?.[alias.platform] === alias.id}
                        onRemove={() => void removeAlias(alias)}
                        onSetPreferred={() => void setPreferred(alias)}
                      />
                    ))}
                  </div>
                )}
              </section>

              <section className="z-settings-people__connect" aria-labelledby="connect-alias-title">
                <div className="z-settings-people__block-heading">
                  <div>
                    <h4 id="connect-alias-title">Vincular un alias</h4>
                    <p>
                      Busca por nombre, usuario o alias. No necesitas conocer el identificador de la
                      cuenta.
                    </p>
                  </div>
                </div>
                <div className="z-settings-people__platform-row">
                  <label
                    htmlFor="settings-people-platform"
                    className="z-settings-people__field-label"
                  >
                    Plataforma
                  </label>
                  <select
                    id="settings-people-platform"
                    aria-label="Plataforma"
                    value={platform}
                    onChange={(event) => {
                      setPlatform(event.target.value as PersonProvider);
                      setSelectedCandidate(null);
                      setSearchQuery('');
                      setCandidates([]);
                    }}
                    style={inputStyle}
                  >
                    <option value="slack">Slack</option>
                    <option value="jira">Jira</option>
                    <option value="github">GitHub</option>
                  </select>
                </div>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    submitProviderSearch();
                  }}
                >
                  <ProviderAccountAutocomplete
                    platform={platform}
                    query={searchQuery}
                    candidates={availableCandidates}
                    searching={searching}
                    onQueryChange={(value) => {
                      setSearchQuery(value);
                      setSelectedCandidate(null);
                    }}
                    onSubmit={submitProviderSearch}
                    onSelect={(candidate) => {
                      setSelectedCandidate(candidate);
                      setSearchQuery('');
                      setCandidates([]);
                      setStatus(`${candidate.displayName} seleccionado. Confirma para vincularlo.`);
                    }}
                  />
                </form>
                {selectedCandidate ? (
                  <div
                    className="z-settings-people__selected-candidate"
                    data-testid="settings-people-selected-candidate"
                  >
                    <div>
                      <span>Cuenta seleccionada</span>
                      <strong>{selectedCandidate.displayName}</strong>
                      <small>
                        {accountLabel(selectedCandidate)} ·{' '}
                        {providerLabel(selectedCandidate.platform)}
                      </small>
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      className="z-settings-people__button"
                      onClick={() => void attachCandidate(selectedCandidate)}
                    >
                      Vincular cuenta
                    </Button>
                  </div>
                ) : null}
                <details
                  open={manualOpen}
                  onToggle={(event) => setManualOpen(event.currentTarget.open)}
                >
                  <summary>Agregar alias manualmente</summary>
                  <form onSubmit={attachManual} className="z-settings-people__manual-form">
                    <p>
                      Úsalo solo si la búsqueda de la plataforma no devuelve la cuenta. El
                      identificador se guarda de forma segura y no se muestra en la lista.
                    </p>
                    <label
                      htmlFor="settings-people-manual-alias"
                      className="z-settings-people__field-label"
                    >
                      Alias o nombre visible
                    </label>
                    <input
                      id="settings-people-manual-alias"
                      aria-label="Alias o nombre visible"
                      value={manualAliasName}
                      onChange={(event) => setManualAliasName(event.target.value)}
                      placeholder="Por ejemplo, @ada-lovelace"
                      style={inputStyle}
                    />
                    <label
                      htmlFor="settings-people-manual-id"
                      className="z-settings-people__field-label"
                    >
                      Identificador técnico
                    </label>
                    <input
                      id="settings-people-manual-id"
                      aria-label="Identificador técnico"
                      value={manualExternalId}
                      onChange={(event) => setManualExternalId(event.target.value)}
                      placeholder="Solo si la plataforma lo solicita"
                      style={inputStyle}
                    />
                    <Button
                      type="submit"
                      size="sm"
                      variant="ghost"
                      className="z-settings-people__button"
                    >
                      Guardar alias manual
                    </Button>
                  </form>
                </details>
              </section>

              <details className="z-settings-people__management">
                <summary>Organizar aliases y personas</summary>
                <div className="z-settings-people__management-grid">
                  <form onSubmit={merge}>
                    <h4>Unir personas</h4>
                    <p>Conserva esta persona y mueve aquí los aliases de otra.</p>
                    <select
                      aria-label="Persona que se va a unir"
                      value={mergeSourceId}
                      onChange={(event) => setMergeSourceId(event.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Elige una persona…</option>
                      {otherPeople.map((person) => (
                        <option key={person.id} value={person.id}>
                          {person.displayName}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="submit"
                      size="sm"
                      variant="ghost"
                      className="z-settings-people__button"
                      disabled={!mergeSourceId}
                    >
                      Unir personas
                    </Button>
                  </form>

                  <form onSubmit={split}>
                    <h4>Separar aliases</h4>
                    <p>Crea otra persona con los aliases seleccionados.</p>
                    <input
                      aria-label="Nombre de la nueva persona separada"
                      value={splitName}
                      onChange={(event) => setSplitName(event.target.value)}
                      placeholder="Nombre opcional"
                      style={inputStyle}
                    />
                    <div className="z-settings-people__checkbox-list">
                      {selected.aliases.map((alias) => (
                        <label key={alias.id}>
                          <input
                            type="checkbox"
                            checked={splitAliasIds.includes(alias.id)}
                            onChange={(event) =>
                              setSplitAliasIds((current) =>
                                event.target.checked
                                  ? [...current, alias.id]
                                  : current.filter((id) => id !== alias.id),
                              )
                            }
                          />
                          {providerLabel(alias.platform)} · {alias.displayName}
                        </label>
                      ))}
                    </div>
                    <Button
                      type="submit"
                      size="sm"
                      variant="ghost"
                      className="z-settings-people__button"
                      disabled={!splitAliasIds.length}
                    >
                      Separar aliases
                    </Button>
                  </form>

                  <form onSubmit={reassign}>
                    <h4>Reasignar un alias</h4>
                    <p>Mueve un alias de esta persona a otra ya existente.</p>
                    <select
                      aria-label="Alias que se va a reasignar"
                      value={reassignAliasId}
                      onChange={(event) => setReassignAliasId(event.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Elige un alias…</option>
                      {selected.aliases.map((alias) => (
                        <option key={alias.id} value={alias.id}>
                          {providerLabel(alias.platform)} · {alias.displayName}
                        </option>
                      ))}
                    </select>
                    <select
                      aria-label="Persona de destino"
                      value={reassignTargetId}
                      onChange={(event) => setReassignTargetId(event.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Elige una persona de destino…</option>
                      {otherPeople.map((person) => (
                        <option key={person.id} value={person.id}>
                          {person.displayName}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="submit"
                      size="sm"
                      variant="ghost"
                      className="z-settings-people__button"
                      disabled={!reassignAliasId || !reassignTargetId}
                    >
                      Reasignar alias
                    </Button>
                  </form>
                </div>
              </details>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
